/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\components\Common\FallbackStatusIndicator.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useState, useEffect } from 'react';
import FallbackNotificationService from '../../services/FallbackNotificationService';

interface ServiceStatus {
  tva: boolean;
  tarifs: boolean;
  fermetures: boolean;
  devis: boolean;
  mousses: boolean;
  entreprise: boolean;
  matieres: boolean;
  clients: boolean;
  calepinage: boolean;
  auth: boolean;
}

const FallbackStatusIndicator: React.FC = () => {
  const [serviceStatus, setServiceStatus] = useState<ServiceStatus>(() => {
    // Charger l'état depuis localStorage au démarrage
    try {
      const saved = localStorage.getItem('fallback-status');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Erreur chargement état fallbacks:', e);
    }
    return {
      tva: false,
      tarifs: false,
      fermetures: false,
      devis: false,
      mousses: false,
      entreprise: false,
      matieres: false,
      clients: false,
      calepinage: false,
      auth: false
    };
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  
  // États pour le drag & drop
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem('fallback-indicator-position');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.warn('Erreur chargement position:', e);
    }
    return { x: window.innerWidth - 350, y: 16 };
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Sauvegarder l'état dans localStorage à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem('fallback-status', JSON.stringify(serviceStatus));
    } catch (e) {
      console.warn('Erreur sauvegarde état fallbacks:', e);
    }
  }, [serviceStatus]);
  
  // Sauvegarder la position dans localStorage
  useEffect(() => {
    try {
      localStorage.setItem('fallback-indicator-position', JSON.stringify(position));
    } catch (e) {
      console.warn('Erreur sauvegarde position:', e);
    }
  }, [position]);

  // Gestion du drag & drop
  const handleMouseDown = (e: React.MouseEvent) => {
    // Ne commencer le drag que si on clique sur la barre de titre (pas sur les boutons)
    const target = e.target as HTMLElement;
    if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.closest('button')) {
      return;
    }
    
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    // Limiter la position dans la fenêtre
    const maxX = window.innerWidth - 100;
    const maxY = window.innerHeight - 100;
    
    setPosition({
      x: Math.max(0, Math.min(newX, maxX)),
      y: Math.max(0, Math.min(newY, maxY))
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    // S'abonner directement au FallbackNotificationService
    const unsubscribe = FallbackNotificationService.subscribeToAlerts((alerts) => {

      // Réinitialiser tous les statuts
      const newStatus = {
        tva: false, tarifs: false, fermetures: false, devis: false, mousses: false,
        entreprise: false, matieres: false, clients: false, calepinage: false, auth: false
      };
      
      // Activer les services qui ont des alertes actives
      alerts.forEach(alert => {
        const type = alert.type.toLowerCase();

        if (type === 'tva' || type === 'entreprise') {
          newStatus.tva = true;
          newStatus.entreprise = true;
        } else if (type === 'tarifs' || type === 'tarif') {
          newStatus.tarifs = true;
        } else if (type === 'fermetures' || type === 'fermeture') {
          newStatus.fermetures = true;
        } else if (type === 'devis') {
          newStatus.devis = true;
        } else if (type === 'mousses' || type === 'mousse') {
          newStatus.mousses = true;
        } else if (type === 'matieres' || type === 'matiere' || type === 'matière') {
          newStatus.matieres = true;
        } else if (type === 'clients' || type === 'client') {
          newStatus.clients = true;
        } else if (type === 'calepinage') {
          newStatus.calepinage = true;
        } else if (type === 'auth' || type === 'utilisateur') {
          newStatus.auth = true;
        }
      });
      
      setServiceStatus(newStatus);
      setLastUpdate(new Date());
    });

    // Écouter les événements de fallback
    const handleFallbackEvent = (event: CustomEvent) => {
      const { type } = event.detail;

      setServiceStatus(prev => ({
        ...prev,
        [type]: true
      }));
      setLastUpdate(new Date());
    };

    // Écouter les événements de récupération (quand l'API fonctionne à nouveau)
    const handleRecoveryEvent = (event: CustomEvent) => {
      const { type } = event.detail;

      setServiceStatus(prev => ({
        ...prev,
        [type]: false
      }));
      setLastUpdate(new Date());
    };

    // Écouter les événements de reset global
    const handleGlobalReset = () => {

      setServiceStatus({
        tva: false, tarifs: false, fermetures: false, devis: false, mousses: false,
        entreprise: false, matieres: false, clients: false, calepinage: false, auth: false
      });
      setLastUpdate(new Date());
    };

    // Ajouter les listeners
    window.addEventListener('fallback-activated', handleFallbackEvent as EventListener);
    window.addEventListener('service-recovered', handleRecoveryEvent as EventListener);
    window.addEventListener('fallbacks-reset', handleGlobalReset as EventListener);

    return () => {
      // Désabonnement du FallbackNotificationService
      unsubscribe();
      
      window.removeEventListener('fallback-activated', handleFallbackEvent as EventListener);
      window.removeEventListener('service-recovered', handleRecoveryEvent as EventListener);
      window.removeEventListener('fallbacks-reset', handleGlobalReset as EventListener);
    };
  }, []);

  const detectFallbackFromLog = (message: string) => {
    const lowerMessage = message.toLowerCase();
    
    // Détecter les fallbacks basés sur les messages de log
    // Patterns à détecter : "fallback", "utilisation fallback", "🚨 FALLBACK", etc.
    if (lowerMessage.includes('fallback') || lowerMessage.includes('utilisation fallback') || lowerMessage.includes('🚨 fallback')) {

      // Détecter le type de service depuis le message
      if (lowerMessage.includes('tva') || lowerMessage.includes('entreprise')) {

        setServiceStatus(prev => ({ ...prev, tva: true, entreprise: true }));
      }
      if (lowerMessage.includes('tarif')) {

        setServiceStatus(prev => ({ ...prev, tarifs: true }));
      }
      if (lowerMessage.includes('fermeture')) {

        setServiceStatus(prev => ({ ...prev, fermetures: true }));
      }
      if (lowerMessage.includes('devis')) {

        setServiceStatus(prev => ({ ...prev, devis: true }));
      }
      if (lowerMessage.includes('mousse')) {

        setServiceStatus(prev => ({ ...prev, mousses: true }));
      }
      if (lowerMessage.includes('matiere') || lowerMessage.includes('matière')) {

        setServiceStatus(prev => ({ ...prev, matieres: true }));
      }
      if (lowerMessage.includes('client')) {

        setServiceStatus(prev => ({ ...prev, clients: true }));
      }
      if (lowerMessage.includes('calepinage') || lowerMessage.includes('placement simple')) {

        setServiceStatus(prev => ({ ...prev, calepinage: true }));
      }
      if (lowerMessage.includes('auth') || lowerMessage.includes('utilisateur')) {

        setServiceStatus(prev => ({ ...prev, auth: true }));
      }
      
      setLastUpdate(new Date());
    }
  };

  const getStatusIcon = (isActive: boolean, criticality: 'critical' | 'high' | 'medium' | 'low') => {
    if (!isActive) return '🟢';
    
    switch (criticality) {
      case 'critical': return '🔴';
      case 'high': return '🟠';
      case 'medium': return '🟡';
      case 'low': return '🟢';
      default: return '⚪';
    }
  };

  const services = [
    { key: 'tva', label: 'TVA', criticality: 'critical' as const },
    { key: 'tarifs', label: 'Tarifs', criticality: 'high' as const },
    { key: 'fermetures', label: 'Fermetures', criticality: 'high' as const },
    { key: 'devis', label: 'Devis', criticality: 'high' as const },
    { key: 'mousses', label: 'Mousses', criticality: 'high' as const },
    { key: 'entreprise', label: 'Entreprise', criticality: 'high' as const },
    { key: 'matieres', label: 'Matières', criticality: 'medium' as const },
    { key: 'clients', label: 'Clients', criticality: 'medium' as const },
    { key: 'calepinage', label: 'Calepinage', criticality: 'medium' as const },
    { key: 'auth', label: 'Auth', criticality: 'medium' as const }
  ];

  const activeFallbacks = services.filter(service => serviceStatus[service.key as keyof ServiceStatus]);
  const criticalCount = activeFallbacks.filter(s => s.criticality === 'critical').length;
  const highCount = activeFallbacks.filter(s => s.criticality === 'high').length;

  // Ne pas afficher si aucun fallback actif et pas en mode développement
  const isDev = process.env.NODE_ENV === 'development';
  if (!isDev && activeFallbacks.length === 0) return null;

  return (
    <div 
      className="fixed z-50"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      {/* Indicateur compact */}
      <div 
        className={`
          bg-white border-2 rounded-lg shadow-lg transition-all duration-300
          ${criticalCount > 0 ? 'border-red-500' : highCount > 0 ? 'border-orange-500' : 'border-green-500'}
          ${isExpanded ? 'w-80' : 'w-auto'}
          ${isDragging ? 'shadow-2xl' : ''}
        `}
        onMouseDown={handleMouseDown}
      >
        {/* Indicateur visuel de drag */}
        <div className="absolute top-1 left-1/2 transform -translate-x-1/2 cursor-grab active:cursor-grabbing">
          <div className="flex space-x-1">
            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
            <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          </div>
        </div>
        {/* Vue compacte */}
        {!isExpanded && (
          <div 
            className="p-3 pt-4 flex items-center space-x-2 cursor-pointer"
            onClick={(e) => {
              if (!isDragging) {
                setIsExpanded(!isExpanded);
              }
            }}
          >
            <span className="text-sm font-medium">Fallbacks:</span>
            <div className="flex space-x-1">
              {services.map(service => (
                <span key={service.key} className="text-lg">
                  {getStatusIcon(serviceStatus[service.key as keyof ServiceStatus], service.criticality)}
                </span>
              ))}
            </div>
            {activeFallbacks.length > 0 && (
              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
                {activeFallbacks.length}
              </span>
            )}
          </div>
        )}

        {/* Vue étendue */}
        {isExpanded && (
          <div className="p-4 pt-5">
            <div className="flex justify-between items-center mb-3 cursor-move">
              <h3 className="font-bold text-gray-800 select-none">État des Services</h3>
              <div className="flex items-center gap-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(false);
                  }}
                  className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                  title="Réduire"
                >
                  ▼
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    setServiceStatus({
                      tva: false, tarifs: false, fermetures: false, devis: false, mousses: false,
                      entreprise: false, matieres: false, clients: false, calepinage: false, auth: false
                    });
                  }}
                  className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded"
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Statistiques */}
            <div className="mb-3 text-xs text-gray-600">
              <div>Actifs: {activeFallbacks.length}/10</div>
              <div>Dernière MAJ: {lastUpdate.toLocaleTimeString()}</div>
            </div>

            {/* Liste des services */}
            <div className="space-y-2">
              {services.map(service => {
                const isActive = serviceStatus[service.key as keyof ServiceStatus];
                return (
                  <div 
                    key={service.key}
                    className={`
                      flex items-center justify-between p-2 rounded text-sm
                      ${isActive 
                        ? service.criticality === 'critical' ? 'bg-red-50 border border-red-200' 
                          : service.criticality === 'high' ? 'bg-orange-50 border border-orange-200'
                          : 'bg-yellow-50 border border-yellow-200'
                        : 'bg-green-50 border border-green-200'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">
                        {getStatusIcon(isActive, service.criticality)}
                      </span>
                      <span className="font-medium">{service.label}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`
                        text-xs px-2 py-1 rounded
                        ${service.criticality === 'critical' ? 'bg-red-100 text-red-800'
                          : service.criticality === 'high' ? 'bg-orange-100 text-orange-800'
                          : service.criticality === 'medium' ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                        }
                      `}>
                        {service.criticality.toUpperCase()}
                      </span>
                      {isActive && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setServiceStatus(prev => ({ ...prev, [service.key]: false }));
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex justify-between text-xs">
                <button
                  onClick={(e) => {
                    e.stopPropagation();

                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  Log État
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Test de fallback
                    setServiceStatus(prev => ({ ...prev, tarifs: true, fermetures: true }));
                  }}
                  className="text-purple-600 hover:text-purple-800"
                >
                  Test
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FallbackStatusIndicator;
